import { useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type {
  CountAssortmentItem,
  CountItemView,
} from '@/api/endpoints/inventory-count-sessions'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

interface CountOpenListSheetProps {
  open: boolean
  onClose: () => void
  /** Must-count articles with no capture yet, as of the assortment fetched at session start. */
  openItems: Array<CountAssortmentItem>
  capturedItems: Array<CountItemView>
  /**
   * SKUs in the order this device captured them, most recent first. The API orders session items
   * by SKU and carries no per-item capture timestamp, so "newest first" can only cover what this
   * browser session did itself; anything from before a reload falls back to name order.
   */
  recentSkus: Array<string>
  onPickOpen: (item: CountAssortmentItem) => void
  onPickCaptured: (item: CountItemView) => void
}

/**
 * What is still outstanding, and what has been counted.
 *
 * This is the answer to "completeness is invisible until review": before it, the only signal
 * while counting was a progress figure, so the counter walked the store hoping they got
 * everything and found the misses on the review screen.
 *
 * The Offen view carries names only — never a quantity, and never the expected quantity. A list
 * of articles with their system quantities next to them would turn walking the store into
 * confirming the numbers the screen already shows.
 */
export function CountOpenListSheet({
  open,
  onClose,
  openItems,
  capturedItems,
  recentSkus,
  onPickOpen,
  onPickCaptured,
}: CountOpenListSheetProps) {
  const { t } = useTranslation('inventory-count')
  const [tab, setTab] = useState<'open' | 'done'>('open')

  const uncategorized = t('list.uncategorized')
  const groups = useMemo(
    () => groupByCategory(openItems, uncategorized),
    [openItems, uncategorized],
  )
  const captured = useMemo(
    () => sortByRecency(capturedItems, recentSkus),
    [capturedItems, recentSkus],
  )

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <DrawerContent className="h-[80svh]">
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle className="sr-only">{t('list.sheetTitle')}</DrawerTitle>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={tab === 'open' ? 'default' : 'outline'}
              className="h-12 tabular-nums"
              aria-pressed={tab === 'open'}
              onClick={() => setTab('open')}
            >
              {t('list.tabOpen', { open: openItems.length })}
            </Button>
            <Button
              variant={tab === 'done' ? 'default' : 'outline'}
              className="h-12 tabular-nums"
              aria-pressed={tab === 'done'}
              onClick={() => setTab('done')}
            >
              {t('list.tabDone', { done: capturedItems.length })}
            </Button>
          </div>
          <DrawerDescription>
            {tab === 'open' ? t('list.hintOpen') : t('list.hintDone')}
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === 'open' && (
            <>
              {openItems.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('list.emptyOpen')}
                </p>
              )}
              {groups.map((group) => (
                <div key={group.label ?? ''}>
                  {group.label != null && (
                    <p className="border-b border-border bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </p>
                  )}
                  {group.items.map((item) => (
                    <Row
                      key={item.sku}
                      name={item.name}
                      onClick={() => onPickOpen(item)}
                    />
                  ))}
                </div>
              ))}
            </>
          )}

          {tab === 'done' && (
            <>
              {captured.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('list.emptyDone')}
                </p>
              )}
              {captured.map((item) => (
                <Row
                  key={item.sku}
                  name={item.name}
                  quantity={item.countedQty}
                  onClick={() => onPickCaptured(item)}
                />
              ))}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

interface CategoryGroup {
  /** null renders no header: either the whole list is uncategorized, or this is the single group. */
  label: string | null
  items: Array<CountAssortmentItem>
}

/**
 * Groups by category name, with the uncategorized rest last. An assortment where nothing carries
 * a category renders flat rather than under one meaningless "Ohne Kategorie" header.
 */
function groupByCategory(
  items: Array<CountAssortmentItem>,
  uncategorizedLabel: string,
): Array<CategoryGroup> {
  const named = new Map<string, Array<CountAssortmentItem>>()
  const rest: Array<CountAssortmentItem> = []
  for (const item of items) {
    if (item.category != null && item.category !== '') {
      const bucket = named.get(item.category)
      if (bucket) bucket.push(item)
      else named.set(item.category, [item])
    } else {
      rest.push(item)
    }
  }

  const byName = (a: CountAssortmentItem, b: CountAssortmentItem) =>
    a.name.localeCompare(b.name, 'de')

  if (named.size === 0) return [{ label: null, items: rest.sort(byName) }]

  const groups: Array<CategoryGroup> = [...named.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'de'))
    .map(([label, groupItems]) => ({ label, items: groupItems.sort(byName) }))
  if (rest.length > 0) {
    groups.push({ label: uncategorizedLabel, items: rest.sort(byName) })
  }
  return groups
}

function sortByRecency(
  items: Array<CountItemView>,
  recentSkus: Array<string>,
): Array<CountItemView> {
  const rank = new Map(recentSkus.map((sku, index) => [sku, index]))
  return [...items].sort((a, b) => {
    const ra = rank.get(a.sku)
    const rb = rank.get(b.sku)
    if (ra != null && rb != null) return ra - rb
    if (ra != null) return -1
    if (rb != null) return 1
    return a.name.localeCompare(b.name, 'de')
  })
}

function Row({
  name,
  quantity,
  onClick,
}: {
  name: string
  quantity?: number | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-14 w-full items-center gap-3 border-b border-border px-4 py-2 text-left last:border-0"
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        {name}
      </span>
      {quantity != null && (
        <span className="shrink-0 text-lg font-semibold tabular-nums">
          {formatStockDisplay(quantity)}
        </span>
      )}
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
    </button>
  )
}
